import { useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Code2,
  Link as LinkIcon,
  Image as ImageIcon,
  Minus,
  Undo2,
  Redo2,
} from 'lucide-react';
import clsx from 'clsx';
import { api } from '../../lib/api';

interface Props {
  editor: Editor | null;
  chapterId: string;
}

export default function EditorToolbar({ editor, chapterId }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  if (!editor) return null;

  const handleImage = async (file: File) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('chapter_id', chapterId);
      form.append('file', file);
      const res = await api.form<{ url: string }>('/upload/inline-image', form);
      editor.chain().focus().setImage({ src: res.url, alt: file.name }).run();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload selhal';
      alert(`Nahrání selhalo: ${msg}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleLink = () => {
    const existing = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL odkazu:', existing ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="sticky top-[72px] z-10 -mx-2 mb-4 flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-white/95 px-2 py-2 backdrop-blur">
      <Group>
        <Btn
          icon={<Bold className="h-4 w-4" />}
          label="Tučně"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <Btn
          icon={<Italic className="h-4 w-4" />}
          label="Kurzíva"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <Btn
          icon={<Code className="h-4 w-4" />}
          label="Inline code"
          active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
        />
      </Group>

      <Separator />

      <Group>
        <Btn
          icon={<Heading1 className="h-4 w-4" />}
          label="H1"
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        />
        <Btn
          icon={<Heading2 className="h-4 w-4" />}
          label="H2"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        />
        <Btn
          icon={<Heading3 className="h-4 w-4" />}
          label="H3"
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        />
      </Group>

      <Separator />

      <Group>
        <Btn
          icon={<List className="h-4 w-4" />}
          label="Odrážky"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <Btn
          icon={<ListOrdered className="h-4 w-4" />}
          label="Číslovaný seznam"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <Btn
          icon={<Quote className="h-4 w-4" />}
          label="Blockquote"
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <Btn
          icon={<Code2 className="h-4 w-4" />}
          label="Code block"
          active={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        />
      </Group>

      <Separator />

      <Group>
        <Btn
          icon={<LinkIcon className="h-4 w-4" />}
          label="Odkaz"
          active={editor.isActive('link')}
          onClick={handleLink}
        />
        <Btn
          icon={<ImageIcon className="h-4 w-4" />}
          label={uploading ? 'Nahrávám…' : 'Obrázek'}
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleImage(f);
          }}
        />
        <Btn
          icon={<Minus className="h-4 w-4" />}
          label="Horizontální čára"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        />
      </Group>

      <div className="ml-auto flex gap-0.5">
        <Btn
          icon={<Undo2 className="h-4 w-4" />}
          label="Zpět"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        />
        <Btn
          icon={<Redo2 className="h-4 w-4" />}
          label="Vpřed"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        />
      </div>
    </div>
  );
}

function Group({ children }: { children: React.ReactNode }) {
  return <div className="flex gap-0.5">{children}</div>;
}

function Separator() {
  return <div className="mx-1 h-5 w-px bg-gray-200" />;
}

function Btn({
  icon,
  label,
  active,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'grid h-8 w-8 place-items-center rounded-md transition-colors',
        active ? 'bg-teal-light text-teal' : 'text-gray-500 hover:bg-gray-100 hover:text-dark',
        disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent hover:text-gray-500',
      )}
    >
      {icon}
    </button>
  );
}
